import type { LucideIcon } from 'lucide-react';
import { CircleAlert, Info, TriangleAlert } from 'lucide-react';
import { Children, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import s from './Callout.module.css';

const ICONS: Record<string, LucideIcon> = { info: Info, warning: TriangleAlert, danger: CircleAlert, neutral: Info };

/** Whether a node renders something: not null/undefined/boolean, not an empty or blank string. */
function hasContent(node: ReactNode): boolean {
  return Children.toArray(node).some((c) => (typeof c === 'string' ? c.trim() !== '' : true));
}

/**
 * An inline message with a left accent rule (the books' callout). Renders nothing when it has
 * neither a title nor text (an icon alone says nothing — e.g. a server message that came back empty).
 */
export function Callout({
  tone = 'info',
  title,
  children,
  actions,
  icon,
  className,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'neutral';
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  icon?: LucideIcon;
  className?: string;
}) {
  const Icon = icon ?? ICONS[tone] ?? Info;
  const hasTitle = hasContent(title);
  const hasText = hasContent(children);
  if (!hasTitle && !hasText) return null;
  return (
    <div className={cx(s.callout, s[tone], className)} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon className={s.icon} aria-hidden />
      <div className={s.body}>
        {hasTitle && <p className={s.title}>{title}</p>}
        {hasText && <div className={s.text}>{children}</div>}
      </div>
      {actions && <div className={s.actions}>{actions}</div>}
    </div>
  );
}
